import { useAtomValue } from "jotai";
import { Accordion as AccordionPrimitive } from "radix-ui";
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useParams } from "react-router-dom";

import { ArrowDownIcon } from "~/components/ui/icons/arrow-down";
import { ExternalLinkSimpleIcon } from "~/components/ui/icons/external-link-simple";
import { useOrdersLinkedQuery } from "~/graphql/graphql-new.gen";
import type { BrandMatch } from "~/routes/root";
import { activeSaleState } from "~/stores/sales";

type TrackingOrdersLinkedProps = {
  parcel?: {
    trackingCourier: {
      name: string;
      logoUrl: string;
      slug: string;
    };
    trackingNumber: string;
    trackingUrl: string;
  };
};

export const TrackingOrdersLinked: React.FC<TrackingOrdersLinkedProps> = ({
  parcel,
}) => {
  const { search } = useLocation();
  const { brandId, orderId } = useParams<BrandMatch>();
  const activeSale = useAtomValue(activeSaleState);
  const { t } = useTranslation();

  const ordersLinked = useOrdersLinkedQuery(
    {
      input: {
        saleId: activeSale?.id ?? "",
        trackingCourierSlug: parcel?.trackingCourier.slug ?? "",
        trackingNumber: parcel?.trackingNumber ?? "",
      },
    },
    {
      enabled: !!activeSale?.id && !!parcel?.trackingNumber,
    },
  );

  if (
    !ordersLinked ||
    (ordersLinked.data?.ordersByTracking.filter((order) => order.id !== orderId)
      .length ?? 0) === 0
  )
    return null;

  return (
    <AccordionPrimitive.Root type="single" collapsible>
      <AccordionPrimitive.Item value="orders-linked">
        <AccordionPrimitive.Header>
          <AccordionPrimitive.Trigger className="flex w-full space-x-2 text-left text-xs text-gray-700 [&[data-state=open]>svg]:rotate-180 [&[data-state=open]]:pb-2">
            <span>
              {t("order.tracking.x_other_order_same_parcel", {
                count: ordersLinked.data?.ordersByTracking.length,
              })}
            </span>
            <ArrowDownIcon className="size-4 text-[#767A7C] transition-transform" />
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
        <AccordionPrimitive.Content>
          <ul className="space-y-2">
            {ordersLinked.data?.ordersByTracking
              .filter((order) => order.id !== orderId)
              .map((order) => (
                <li key={order.id}>
                  <NavLink
                    to={`/${brandId}/orders/${order.id}${search}`}
                    target="_blank"
                    className="flex items-center space-x-1 text-sm font-semibold text-green-900"
                  >
                    <span>{order.id}</span>
                    <div className="shrink-0">
                      <ExternalLinkSimpleIcon className="size-3.5" />
                    </div>
                  </NavLink>
                </li>
              ))}
          </ul>
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    </AccordionPrimitive.Root>
  );
};
